/**
 * This file was imported from @doxyme/next-utils in an effort to reduce the burder of co library dependencies.
 */
export enum DataDogPrivacyOptions {
  'Allow' = 'allow',
  'Mask' = 'mask',
  'Hidden' = 'hidden',
  'MaskUserInput' = 'mask-user-input',
}

export const DEFAULT_ACTION_NAME = 'masked-action';

export type DataDogPrivacyAttributesConfig<
  PrivacyType extends DataDogPrivacyOptions,
> = {
  'data-dd-privacy': PrivacyType;
  'data-dd-action-name': string;
};

/**
 * Utility that creates an object that, when spread (`...`) as props/attributes across
 * a TSX tag, will tell DataDog RUM how to handle the text/input values of the element.
 * @param privacyPolicy The type of privacy policy to apply to the element
 * @param actionName Name that will be displayed for action in the session replay
 * @returns An object with a data-dd-privacy property set to the appropriate privacyPolicy string
 *   and data-dd-action-name set to provided or default string
 */
export const createDataDogAttributes = <
  PrivacyType extends DataDogPrivacyOptions,
>(
  privacyPolicy: PrivacyType,
  actionName = DEFAULT_ACTION_NAME,
): DataDogPrivacyAttributesConfig<PrivacyType> => ({
  'data-dd-privacy': privacyPolicy,
  'data-dd-action-name': actionName,
});

/**
 * Utility that creates an object that, when spread (`...`) as props/attributes across
 * a TSX tag, will set DataDog RUM-specific props to mask text/input values.
 * @returns An object with a data-dd-privacy property set to "mask"
 *   and data-dd-action-name set to provided or default string
 *
 * @example
 * ```tsx
 * <p {...maskDataDog()}>Private Information</p>
 * ```
 * @example 2
 * ```tsx
 * <p {...maskDataDog('someName')}>Private Information</p>
 * ```
 */
export const maskDataDog = (
  actionName?: string,
): DataDogPrivacyAttributesConfig<DataDogPrivacyOptions.Mask> =>
  createDataDogAttributes(DataDogPrivacyOptions.Mask, actionName);
